import React, { useState, useRef, useEffect, useCallback } from 'react';
import { marked } from 'marked';
import './RichTextEditor.css';

marked.setOptions({ breaks: true, gfm: true });

const RichTextEditor = ({ value, onChange, placeholder = 'Enter content...' }) => {
  const [mode, setMode] = useState('wysiwyg'); // 'wysiwyg' | 'html' | 'markdown'
  const [htmlDraft, setHtmlDraft] = useState('');
  const [mdText, setMdText] = useState('');
  const editorRef = useRef(null);
  const isUpdatingRef = useRef(false);

  // Sync external value → contentEditable (only in wysiwyg mode)
  useEffect(() => {
    if (mode === 'wysiwyg' && editorRef.current && !isUpdatingRef.current) {
      if (editorRef.current.innerHTML !== (value || '')) {
        const hadFocus = document.activeElement === editorRef.current;
        editorRef.current.innerHTML = value || '';
        if (hadFocus) editorRef.current.focus();
      }
    }
  }, [value, mode]);

  const handleContentChange = useCallback(() => {
    if (editorRef.current) {
      isUpdatingRef.current = true;
      onChange(editorRef.current.innerHTML);
      setTimeout(() => { isUpdatingRef.current = false; }, 10);
    }
  }, [onChange]);

  const execCommand = useCallback((command, val = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, val);
    handleContentChange();
  }, [handleContentChange]);

  const switchToWysiwyg = useCallback(() => {
    setMode('wysiwyg');
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = value || '';
    }, 0);
  }, [value]);

  const switchToHtml = useCallback(() => {
    const current = editorRef.current ? editorRef.current.innerHTML : (value || '');
    setHtmlDraft(current);
    onChange(current);
    setMode('html');
  }, [value, onChange]);

  const switchToMarkdown = useCallback(() => {
    setMdText('');
    setMode('markdown');
  }, []);

  const applyMarkdown = useCallback(() => {
    const html = marked.parse(mdText).trim();
    onChange(html);
    setMode('wysiwyg');
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = html;
    }, 0);
  }, [mdText, onChange]);

  const mdPreviewHtml = mdText ? marked.parse(mdText) : '';

  return (
    <div className="rich-text-editor">
      {/* Mode tab bar */}
      <div className="editor-tab-bar">
        <button
          type="button"
          className={`editor-tab${mode === 'wysiwyg' ? ' active' : ''}`}
          onClick={switchToWysiwyg}
          title="WYSIWYG editor"
        >
          ✏ Write
        </button>
        <button
          type="button"
          className={`editor-tab editor-tab-md${mode === 'markdown' ? ' active' : ''}`}
          onClick={switchToMarkdown}
          title="Paste or write Markdown and convert to HTML"
        >
          ↓ Markdown
        </button>
        <button
          type="button"
          className={`editor-tab editor-tab-html${mode === 'html' ? ' active' : ''}`}
          onClick={switchToHtml}
          title="Edit raw HTML source"
        >
          &lt;/&gt; HTML
        </button>
      </div>

      {/* WYSIWYG toolbar */}
      {mode === 'wysiwyg' && (
        <div className="editor-toolbar">
          <div className="toolbar-group">
            <button type="button" className="toolbar-btn" onClick={() => execCommand('bold')} title="Bold">
              <strong>B</strong>
            </button>
            <button type="button" className="toolbar-btn" onClick={() => execCommand('italic')} title="Italic">
              <em>I</em>
            </button>
            <button type="button" className="toolbar-btn" onClick={() => execCommand('underline')} title="Underline">
              <u>U</u>
            </button>
          </div>

          <div className="toolbar-group">
            <button type="button" className="toolbar-btn" onClick={() => execCommand('formatBlock', 'h2')} title="Heading 2">
              H2
            </button>
            <button type="button" className="toolbar-btn" onClick={() => execCommand('formatBlock', 'h3')} title="Heading 3">
              H3
            </button>
            <button type="button" className="toolbar-btn" onClick={() => execCommand('formatBlock', 'p')} title="Paragraph">
              P
            </button>
          </div>

          <div className="toolbar-group">
            <button type="button" className="toolbar-btn" onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
              •
            </button>
            <button type="button" className="toolbar-btn" onClick={() => execCommand('insertOrderedList')} title="Numbered List">
              1.
            </button>
          </div>

          <div className="toolbar-group">
            <button
              type="button"
              className="toolbar-btn"
              onClick={() => {
                const url = prompt('Enter URL:');
                if (url) execCommand('createLink', url);
              }}
              title="Insert Link"
            >
              🔗
            </button>
          </div>
        </div>
      )}

      {/* WYSIWYG editable area */}
      {mode === 'wysiwyg' && (
        <div
          ref={editorRef}
          className="editor-content"
          contentEditable
          onInput={handleContentChange}
          suppressContentEditableWarning
          data-placeholder={placeholder}
        />
      )}

      {/* HTML source editor */}
      {mode === 'html' && (
        <textarea
          className="html-source-editor"
          value={htmlDraft}
          onChange={e => { setHtmlDraft(e.target.value); onChange(e.target.value); }}
          placeholder="Edit HTML source here…"
          rows={12}
          spellCheck={false}
        />
      )}

      {/* Markdown paste / import panel */}
      {mode === 'markdown' && (
        <div className="md-import-panel">
          <div className="md-import-hint">
            Paste or write Markdown. The preview updates live. Click <strong>Apply</strong> to convert to HTML and use it in the editor.
          </div>
          <div className="md-import-split">
            <div className="md-import-pane">
              <div className="md-import-pane-label">Markdown</div>
              <textarea
                className="md-import-textarea"
                value={mdText}
                onChange={e => setMdText(e.target.value)}
                placeholder={`## Job Description\n\nWe are looking for…\n\n**Key Responsibilities**\n- Attend meetings\n- Lead community initiatives\n\n**Requirements**\n- Recognized RSMIN citizen\n- Strong communication skills`}
                rows={12}
                spellCheck={false}
                autoFocus
              />
            </div>
            <div className="md-import-pane">
              <div className="md-import-pane-label">Preview</div>
              <div
                className="md-import-preview editor-content"
                dangerouslySetInnerHTML={{
                  __html: mdPreviewHtml || '<em style="color:#9ca3af">Start typing on the left…</em>'
                }}
              />
            </div>
          </div>
          <div className="md-import-actions">
            <button
              type="button"
              className="toolbar-btn md-apply-btn"
              onClick={applyMarkdown}
              disabled={!mdText.trim()}
            >
              ✓ Apply Markdown
            </button>
            <button
              type="button"
              className="toolbar-btn"
              onClick={switchToWysiwyg}
            >
              ✕ Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
